import { useEffect, useRef, useCallback } from "react"
import { motion, AnimatePresence } from "motion/react"
import { appStore } from "../store/appStore"

const AUTO_CLOSE_MS = 7000
const MONO = "'JetBrains Mono', monospace"

export const ErrorToast = () => {
    const { error, setError } = appStore()
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const dismiss = useCallback(() => {
        setError(null)
    }, [setError])

    useEffect(() => {
        if (!error) return

        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(dismiss, AUTO_CLOSE_MS)

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [error, dismiss])

    return (
        <AnimatePresence mode="wait">
            {error && (
                <motion.div
                    key={error}
                    role="alert"
                    initial={{ opacity: 0, y: 24, x: 24 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    exit={{ opacity: 0, y: 12, x: 12, scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 380, damping: 28 }}
                    className="pointer-events-none fixed z-[1000] bottom-4 right-4 left-4 sm:left-auto sm:max-w-[380px]">
                    <div
                        className="pointer-events-auto relative overflow-hidden rounded-[20px] border border-white/12"
                        style={{
                            background: "linear-gradient(155deg, rgba(30,47,88,0.97) 0%, rgba(26,20,56,0.98) 100%)",
                            boxShadow: "0 16px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(228,32,107,0.15), 0 0 32px rgba(228,32,107,0.12)",
                            backdropFilter: "blur(12px)",
                        }}>
                        {/* accent glow */}
                        <div
                            className="pointer-events-none absolute -top-8 -right-8 size-24 rounded-full blur-2xl opacity-40"
                            style={{ background: "#e4206b" }}
                        />

                        <div className="relative flex gap-3 px-4 pt-4 pb-5 pr-12">
                            <div
                                className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border border-[#e4206b]/40 text-[15px]"
                                style={{ background: "rgba(228,32,107,0.18)" }}>
                                ✕
                            </div>
                            <div className="min-w-0 flex-1">
                                <p
                                    className="mb-1 text-[10px] uppercase tracking-[0.14em] text-[#e4206b]"
                                    style={{ fontFamily: MONO, fontWeight: 700 }}>
                                    Ошибка
                                </p>
                                <p
                                    className="text-[13px] leading-snug text-white/90"
                                    style={{ fontFamily: MONO, fontWeight: 600 }}>
                                    {error}
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            aria-label="Закрыть"
                            onClick={dismiss}
                            className="absolute right-3 top-3 flex size-7 cursor-pointer items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white/50 transition-colors hover:border-white/25 hover:bg-white/[0.12] hover:text-white">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                                <path
                                    d="M1 1l8 8M9 1L1 9"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>

                        {/* progress bar */}
                        <motion.div
                            className="absolute bottom-0 left-0 h-[3px] w-full origin-left"
                            style={{
                                background: "linear-gradient(90deg, #e4206b, #ff6b9d)",
                                boxShadow: "0 0 10px rgba(228,32,107,0.7)",
                            }}
                            initial={{ scaleX: 1 }}
                            animate={{ scaleX: 0 }}
                            transition={{ duration: AUTO_CLOSE_MS / 1000, ease: "linear" }}
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
